/**
 * <AdminLogin> — pagina de login admin (email/senha + MFA opcional).
 * Onda 1B do hardening (junho/2026).
 */

import React, { useState } from 'react';
import { signInAdmin, completeMfaSignIn } from './adminAuth';

export default function AdminLogin({ title }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaPending, setMfaPending] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) {
      setError('Informe email e senha.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await signInAdmin(email.trim(), password);
      if (result.mfaRequired) {
        setMfaPending({ resolver: result.resolver, hint: result.totpHint });
        return;
      }
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        setError('Email ou senha inválidos.');
      } else if (code === 'auth/too-many-requests') {
        setError('Muitas tentativas. Tente novamente em alguns minutos.');
      } else {
        setError(err?.message || 'Falha ao entrar.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfa = async (e) => {
    e?.preventDefault?.();
    if (!mfaCode || mfaCode.length < 6) {
      setError('Digite o código de 6 dígitos.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await completeMfaSignIn(mfaPending.resolver, mfaPending.hint, mfaCode);
      // observeAdmin no AdminGuard cuida da transicao para children.
    } catch (err) {
      setError(err?.message || 'Código inválido.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={fullScreen}>
      <form style={card} onSubmit={mfaPending ? handleMfa : handleLogin}>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>
          {title || 'Acesso administrativo'}
        </h2>
        <p style={{ color: '#6b7280', marginTop: 0, fontSize: 14 }}>
          Use suas credenciais de equipe Consulta+.
        </p>

        {!mfaPending && (
          <>
            <label style={label}>Email</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={input}
              disabled={submitting}
              required
            />
            <label style={label}>Senha</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={input}
              disabled={submitting}
              required
            />
          </>
        )}

        {mfaPending && (
          <>
            <p style={{ marginTop: 0 }}>
              Insira o código de 6 dígitos do seu aplicativo autenticador.
            </p>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              style={{ ...input, fontSize: 22, textAlign: 'center', letterSpacing: 4 }}
              disabled={submitting}
              required
            />
          </>
        )}

        {error && <p style={errorBox}>{error}</p>}

        <button type="submit" disabled={submitting} style={btnPrimary}>
          {submitting ? 'Aguarde…' : mfaPending ? 'Confirmar código' : 'Entrar'}
        </button>

        {mfaPending && (
          <button
            type="button"
            onClick={() => {
              setMfaPending(null);
              setMfaCode('');
              setError('');
            }}
            style={btnLink}
          >
            Voltar para login
          </button>
        )}
      </form>
    </div>
  );
}

const fullScreen = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f4f4f5',
  padding: 16,
  fontFamily: "'Segoe UI', Roboto, sans-serif",
};

const card = {
  background: '#fff',
  borderRadius: 12,
  padding: '32px 28px',
  maxWidth: 420,
  width: '100%',
  boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
};

const label = {
  fontSize: 13,
  color: '#374151',
  marginTop: 12,
};

const input = {
  marginTop: 4,
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 15,
  outline: 'none',
};

const errorBox = {
  background: '#fef2f2',
  color: '#b91c1c',
  border: '1px solid #fecaca',
  padding: '8px 12px',
  borderRadius: 8,
  marginTop: 14,
  fontSize: 13,
};

const btnPrimary = {
  marginTop: 18,
  padding: '12px 18px',
  background: '#00A4E0',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 15,
  cursor: 'pointer',
  fontWeight: 600,
};

const btnLink = {
  marginTop: 12,
  padding: '8px 12px',
  background: 'transparent',
  color: '#00A4E0',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
};
